import React, { useRef } from 'react'
import { IProps } from './Interface'
import { Corner } from 'components/balloon'
import { LabelPosition } from 'types'
import { useOutsideClick } from 'utils'
import { Box, useTheme, useMediaQuery, IconButton } from '@material-ui/core'
import { Balloon, FreeText } from 'components'
import cx from 'classnames'
import useStyles from './Styles'

import DeleteOutline from '@material-ui/icons/DeleteOutline'
import handleEnterKey from 'utils/handle-enter-key'

const QuestionBlock: React.FC<IProps> = ({
  edit,
  color,
  title,
  detail,
  onSave,
  onChange,
  onClickOutside,
  onDelete,
  onKeyPressDetail,
  disabled,
  ...props
}) => {
  const theme = useTheme()
  const containerRef = useRef()
  const xsSize = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles({ color, edit })
  const ref = useRef()

  useOutsideClick(ref, () => {
    if (edit) {
      onClickOutside()
    }
  })

  const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault()
    event.stopPropagation()
    onDelete()
  }

  return (
    <div ref={containerRef}>
      <Box
        className={cx({
          [classes.root]: !disabled,
          [classes.rootDisabled]: disabled
        })}
        ref={ref}
        {...props}
      >
        {!!onDelete && (
          <IconButton
            id={`${title.questionKey}-pursuits-delete-button`}
            onClick={onDeleteButtonClick}
            className={cx(classes.deleteButton, disabled ? '' : 'pursuits-delete-button', {
              [classes.hiddenDeleteButton]: !Boolean(edit)
            })}
          >
            <DeleteOutline />
          </IconButton>
        )}

        <Box display="flex">
          <Balloon
            size={xsSize ? 30 : 38}
            corner={xsSize ? Corner.MiddleRight : Corner.BottomLeft}
            shadow={false}
            bgColor={color}
            flexShrink={0}
            mr={2}
          />
          <FreeText
            id={`${detail.questionKey}-pursuits-text-label`}
            rows={6}
            limit={title.limit}
            textSize={16}
            showLimit={true}
            autoFocus={true}
            hideLabel={!edit || !title.label}
            labelPosition={LabelPosition.Top}
            label={title.label}
            disabled={!edit || !title.questionKey}
            initialValue={title.initialValue}
            onSave={value =>
              title.questionKey &&
              onSave({
                response: value,
                questionKey: title.questionKey
              })
            }
            onChange={value =>
              title.questionKey &&
              onChange({
                response: value,
                questionKey: title.questionKey
              })
            }
            onKeyPress={handleEnterKey(
              (value: any) =>
                title.questionKey &&
                onSave({
                  response: value,
                  questionKey: title.questionKey
                })
            )}
            classNames={{
              root: classes.freeTextRoot,
              inputContainer: classes.titleInputContainer,
              labelContainer: classes.labelContainer
            }}
          />
        </Box>
        <Box className={classes.detailBox}>
          <FreeText
            id={`${detail.questionKey}-pursuits-text-detail-label`}
            rows={6}
            limit={detail.limit}
            textSize={16}
            showLimit={true}
            multiline={true}
            autoFocus={Boolean(!title?.questionKey)}
            hideLabel={!edit || !detail.label}
            labelPosition={LabelPosition.Top}
            label={detail.label}
            disabled={!edit}
            initialValue={detail.initialValue}
            onSave={value =>
              detail.questionKey &&
              onSave({
                response: value,
                questionKey: detail.questionKey
              })
            }
            onChange={value =>
              detail.questionKey &&
              onChange({
                response: value,
                questionKey: detail.questionKey
              })
            }
            onKeyPress={onKeyPressDetail}
            classNames={{
              root: classes.freeTextRoot,
              inputContainer: classes.detailInputContainer,
              labelContainer: classes.labelContainer
            }}
          />
        </Box>
      </Box>
    </div>
  )
}

QuestionBlock.defaultProps = {
  edit: false,
  title: {
    label: '',
    questionKey: '',
    initialValue: ''
  },
  detail: {
    label: '',
    questionKey: '',
    initialValue: ''
  },
  onSave: () => {},
  onChange: () => {},
  onClickOutside: () => {},
  onKeyPressDetail: () => {}
}

export default QuestionBlock
